import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy, 
  where 
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Retry logic for network issues
const retryOperation = async (operation, maxRetries = 3) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await operation();
    } catch (error) {
      console.log(`🔄 Attempt ${i + 1} failed:`, error.message);
      
      if ((error.code === 'unavailable' || error.code === 'timeout') && i < maxRetries - 1) {
        // Wait before retry (exponential backoff)
        const delay = Math.pow(2, i) * 1000;
        console.log(`⏱️  Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      throw error;
    }
  }
};

export const firestoreService = {
  // Chapter management
  async createChapter(chapterData) {
    return retryOperation(async () => {
      console.log('🗄️ FirestoreService: Creating chapter document');
      console.log('📄 Chapter data:', chapterData);
      
      const chapterWithTimestamps = {
        ...chapterData,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      console.log('📝 Full chapter data with timestamps:', chapterWithTimestamps);
      console.log('🔗 Adding to Firestore collection: chapters');
      
      const docRef = await addDoc(collection(db, 'chapters'), chapterWithTimestamps);
      
      console.log('✅ FirestoreService: Chapter created with ID:', docRef.id);
      return { success: true, id: docRef.id };
    }).catch(error => {
      console.error('❌ FirestoreService: Error creating chapter:', error);
      console.error('❌ Error code:', error.code);
      console.error('❌ Error message:', error.message);
      return { success: false, error: error.message };
    });
  },

  async getChapters() {
    try {
      const q = query(collection(db, 'chapters'), orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      const chapters = [];
      querySnapshot.forEach((doc) => {
        chapters.push({ id: doc.id, ...doc.data() });
      });
      return { success: true, chapters };
    } catch (error) {
      console.error('Error getting chapters:', error);
      return { success: false, error: error.message };
    }
  },

  async getChaptersByType(type) {
    try {
      const q = query(
        collection(db, 'chapters'), 
        where('type', '==', type),
        orderBy('order', 'asc')
      );
      const querySnapshot = await getDocs(q);
      const chapters = [];
      querySnapshot.forEach((doc) => {
        chapters.push({ id: doc.id, ...doc.data() });
      });
      return { success: true, chapters };
    } catch (error) {
      console.error('Error getting chapters by type:', error);
      return { success: false, error: error.message };
    }
  },

  async updateChapter(chapterId, updates) {
    try {
      const chapterRef = doc(db, 'chapters', chapterId);
      await updateDoc(chapterRef, {
        ...updates,
        updatedAt: new Date()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating chapter:', error);
      return { success: false, error: error.message };
    }
  },

  async deleteChapter(chapterId) {
    try {
      await deleteDoc(doc(db, 'chapters', chapterId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting chapter:', error);
      return { success: false, error: error.message };
    }
  },

  // User management
  async createUserProfile(userId, userData) {
    try {
      const docRef = await addDoc(collection(db, 'users'), {
        userId,
        ...userData,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      return { success: true, id: docRef.id };
    } catch (error) {
      console.error('Error creating user profile:', error);
      return { success: false, error: error.message };
    }
  },

  async getUserProfile(userId) {
    try {
      const q = query(collection(db, 'users'), where('userId', '==', userId));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { success: true, user: { id: doc.id, ...doc.data() } };
      }
      return { success: false, error: 'User not found' };
    } catch (error) {
      console.error('Error getting user profile:', error);
      return { success: false, error: error.message };
    }
  },

  async updateUserProfile(userId, updates) {
    try {
      const userResult = await this.getUserProfile(userId);
      if (userResult.success) {
        const userRef = doc(db, 'users', userResult.user.id);
        await updateDoc(userRef, {
          ...updates,
          updatedAt: new Date()
        });
        return { success: true };
      }
      return { success: false, error: 'User not found' };
    } catch (error) {
      console.error('Error updating user profile:', error);
      return { success: false, error: error.message };
    }
  }
};
